import { useEffect, useState } from 'react';
import { api, type ItemDeMenu } from '../lib/api.js';

/**
 * ── OS LINKS DO DONO CHEGAM A TODA TELA (ordem do dono de 27/09/2026) ───────
 *
 * > *«eu editei o topo, colocando alguns links de menu que ficam do lado do
 * > procurar, e eles aparecem na vitrine da área mas não na da comunidade»*
 *
 * ## O defeito não era da comunidade
 *
 * `Cabecalho` recebe `menu` por prop, com `= []` por omissão, e **um único sítio
 * a passava**: a vitrine. A comunidade, a agenda, «Minhas aulas», a página do
 * curso, a da trilha — todas desenhavam o mesmo cabeçalho sem os links, e
 * nenhuma estava errada por si: *o dado não chegava lá*. Passar a prop na
 * comunidade teria deixado as outras exactamente como estavam, e a tela seguinte
 * nasceria com o mesmo buraco (`vestigo-o-conserto-fica-preso-onde-nasceu`).
 *
 * Então quem busca é o **cabeçalho**, e não quem o desenha.
 *
 * ## O cache é de módulo, e existe por duas razões
 *
 * O app do aluno não tem React Query: `useCarregar` guarda em `useState`, que
 * morre com o componente. Sem cache, cada navegação entre telas repetiria o
 * pedido, e duas instâncias na mesma página fariam dois.
 *
 * `emVoo` é a segunda razão, e a que uma implementação apressada esquece: dois
 * componentes a montar no mesmo render pedem **antes** de o primeiro responder,
 * e um `Map` de resultados não os impede. Guardar a promessa impede.
 *
 * ## O que ele faz quando falha, e por que isso é o certo
 *
 * Devolve lista vazia, calado. O cabeçalho é moldura: um erro nos links do dono
 * não pode derrubar a agenda nem o mural, e uma frase de erro num sítio onde o
 * aluno não pediu nada é pior do que um link a menos. *A tela fica como estava
 * antes desta obra* — que é o pior caso aceitável, e não um estado novo.
 *
 * O cache guarda a lista vazia também: uma área sem links do dono é a maioria
 * delas, e sem isso toda navegação repetiria um pedido para receber `[]`.
 */
/**
 * ── O MESMO PEDIDO PASSOU A RESPONDER DUAS PERGUNTAS (30/09/2026) ──────────
 *
 * Ele trazia só os links. Com a porta do tutor a existir FORA da aula, toda tela
 * da área precisa de saber uma segunda coisa antes de desenhar: **se há a quem
 * perguntar** (`cfg.ia.assistente === "sim"`).
 *
 * Entra aqui, e não num pedido próprio, por três razões:
 *
 * 1. este pedido **já acontece em toda tela** com cache de módulo — um segundo
 *    pedido para trazer um booleano seria uma ida à rede por tela, de graça;
 * 2. as duas respostas são lidas pelo MESMO componente (`Cabecalho`) no mesmo
 *    render, e duas fontes com dois tempos de chegada dariam dois piscares;
 * 3. o sítio onde o defeito dos links nasceu é o mesmo: *o dado não chegava lá*.
 *    Resolver o interruptor tela por tela repetiria a história inteira.
 *
 * **Ausente lê-se como DESLIGADO.** É o valor de omissão em `TOPO_VAZIO`, e é o
 * que faz um servidor antigo, uma falha de rede ou a prévia do Estúdio não
 * desenharem uma porta que o servidor recusaria com 404.
 */
interface DoTopo {
  menu: ItemDeMenu[];
  assistente: boolean;
}

const TOPO_VAZIO: DoTopo = { menu: [], assistente: false };

const porArea = new Map<string, DoTopo>();
const emVoo = new Map<string, Promise<DoTopo>>();

/** Apaga o cache. Existe para as cercas, e nenhuma tela o chama. */
export function esquecerOsLinksDoTopo(): void {
  porArea.clear();
  emVoo.clear();
}

function buscar(areaSlug: string): Promise<DoTopo> {
  const jaVoando = emVoo.get(areaSlug);
  if (jaVoando) return jaVoando;
  const promessa = api
    .get<{ menu?: ItemDeMenu[]; assistente?: boolean }>(`/a/${areaSlug}/topo`)
    .then((r) => ({
      menu: Array.isArray(r?.menu) ? r.menu : [],
      /* `=== true` e não `Boolean(...)`: um servidor que mande a string `"sim"`
         por engano não liga uma porta paga. */
      assistente: r?.assistente === true,
    }))
    .catch(() => TOPO_VAZIO)
    .then((doTopo) => {
      porArea.set(areaSlug, doTopo);
      emVoo.delete(areaSlug);
      return doTopo;
    });
  emVoo.set(areaSlug, promessa);
  return promessa;
}

/**
 * Os links do topo desta área.
 *
 * `daProp` tem **precedência e curto-circuito**: a vitrine já os recebe na
 * resposta dela, e pedir outra vez seria um pedido a mais na tela que mais pesa.
 * Com a prop preenchida este hook não toca a rede — é isso que faz a vitrine
 * continuar a custar exactamente o que custava.
 */
export function useDoTopo(areaSlug: string | null, daProp: Partial<DoTopo>): DoTopo {
  /*
   * ── UM HOOK, E NÃO DOIS, E ISSO NÃO É ESTILO ────────────────────────────
   *
   * A primeira versão desta obra tinha `useLinksDoTopo` e `useAssistenteDoTopo`
   * separados, cada um a chamar este miolo com o SEU campo. Parecia mais
   * arrumado e estava errado: cada instância via um campo só, `temTudo` nunca
   * era verdade, e **a vitrine — que passa os dois — voltava a pedir**. A cerca
   * `os-links-do-topo-chegam-a-toda-tela.test.tsx` apanhou-o na hora, contando
   * o `api.get`, que é exactamente o que ela existe para contar.
   *
   * Então quem sabe se pode calar a rede é quem tem as duas props na mão, e essa
   * é UMA chamada. O curto-circuito continua a ser por CAMPO — quem passou um só
   * pede, e a prop vence no campo que ela traz —, mas a decisão de pedir é uma.
   */
  const temTudo = daProp.menu !== undefined && daProp.assistente !== undefined;
  const [buscado, setBuscado] = useState<DoTopo>(() =>
    areaSlug ? (porArea.get(areaSlug) ?? TOPO_VAZIO) : TOPO_VAZIO,
  );

  useEffect(() => {
    if (temTudo || !areaSlug) return;
    const doCache = porArea.get(areaSlug);
    if (doCache) {
      setBuscado(doCache);
      return;
    }
    /* `vivo` porque a resposta pode chegar depois de a tela sair — escrever num
       componente desmontado é o aviso que ninguém lê até virar defeito. */
    let vivo = true;
    void buscar(areaSlug).then((doTopo) => {
      if (vivo) setBuscado(doTopo);
    });
    return () => {
      vivo = false;
    };
  }, [areaSlug, temTudo]);

  return {
    menu: daProp.menu ?? buscado.menu,
    /*
     * `??` e não `||`: `false` da prop é uma RESPOSTA («o tutor desligou»), e um
     * `||` deixaria o valor do servidor vencê-la. É a diferença entre «ele
     * desligou» e «ninguém me disse».
     */
    assistente: daProp.assistente ?? buscado.assistente,
  };
}
